/**
 * Capture model conversions (PRP-SKETCH-FIELD-1 work item 3, D8).
 *
 * - `toV2(v1)`: rectangle -> 4 corners; primary + second sharing an edge ->
 *   their union outline; anything else -> the primary rectangle plus a
 *   `banner` saying why. v1 openings have no wall or position, so they are kept
 *   verbatim in `unplaced`, never dropped and never guessed onto a wall.
 * - `fromV2ToV1Fields(v2)`: the v1 fields for the existing backfill
 *   (app/api/routes/esx.py): primary/second when the outline is a rectangle or
 *   an L (or still matches the v1 rectangles it came from), and v1 openings.
 * - `roomToV2` / `roomFromV2`: core room <-> v2 document, no field lost.
 */
import { GEOMETRY_VERSION, MANUAL_SOURCE, POLICY, IN_PER_FT } from './policy';
import {
  areaPts, axisOf, clone, dist, findRoom, inPolyPts, round6, MIN_POLY_CORNERS, RECT_CORNERS, L_CORNERS, L_GRID_VALUES,
} from './geom';
import { blankPlan, reindexNext } from './plan';
import { shapePts } from './rooms';
import { addOpening, updateOpening } from './openings';
import { buildUF } from './uf';
import { migrateLegacyWalls } from './migrate';
import { migrateDoors } from './doors';
import { sanitizePlan } from './sanitize';
import type {
  Axis, Corner, Fail, Measurement, OpeningV1, Plan, PlanDefaults, Pt, RectGeom, Room,
  RoomGeometryV1, RoomGeometryV2,
} from './types';

/** Coordinate merge tolerance. v1 fields are rounded to 1e-6 ft each, so a sum
 * (offset + width) can drift by ~2e-6 from the matching side; merge those. */
const EPS = 1e-5;
const close = (a: number, b: number) => Math.abs(a - b) <= EPS;

export interface Rect { x0: number; y0: number; x1: number; y1: number }

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** v1 rectangles in the v1 frame: primary at the origin, second at its offsets. */
export function rectsFromV1(g: { primary?: RectGeom; second?: RectGeom }): Rect[] | null {
  const p = g.primary;
  const pl = num(p?.length), pw = num(p?.width);
  if (pl == null || pw == null || pl <= 0 || pw <= 0) return null;
  const out: Rect[] = [{ x0: 0, y0: 0, x1: pl, y1: pw }];
  const s = g.second;
  if (s) {
    const sl = num(s.length), sw = num(s.width);
    if (sl == null || sw == null || sl <= 0 || sw <= 0) return out;
    const ox = num(s.offsetX) ?? 0, oy = num(s.offsetY) ?? 0;
    out.push({ x0: ox, y0: oy, x1: ox + sl, y1: oy + sw });
  }
  return out;
}

/** Duplicate and collinear corners folded out (as outline_capture.canonical_ring). */
export function canonicalRing(pts: Pt[]): Pt[] {
  const p: Pt[] = [];
  for (const q of pts) {
    const l = p[p.length - 1];
    if (l && close(l.x, q.x) && close(l.y, q.y)) continue;
    p.push({ x: q.x, y: q.y });
  }
  while (p.length > 1 && close(p[0].x, p[p.length - 1].x) && close(p[0].y, p[p.length - 1].y)) p.pop();
  if (p.length < MIN_POLY_CORNERS) return p;
  const out: Pt[] = [];
  const n = p.length;
  for (let i = 0; i < n; i++) {
    const a = p[(i - 1 + n) % n], c = p[i], b = p[(i + 1) % n];
    const cross = (c.x - a.x) * (b.y - c.y) - (c.y - a.y) * (b.x - c.x);
    if (Math.abs(cross) > EPS) out.push(c);
  }
  return out.length ? out : p;
}

/**
 * The ring in a comparable form: translated so its bounding box starts at the
 * origin, positive winding, starting at the min-y then min-x corner, rounded.
 */
export function normalizeRing(pts: Pt[]): Pt[] {
  let ring = canonicalRing(pts);
  if (!ring.length) return ring;
  if (areaPts(ring) < 0) ring = ring.slice().reverse();
  const x0 = Math.min(...ring.map((q) => q.x)), y0 = Math.min(...ring.map((q) => q.y));
  ring = ring.map((q) => ({ x: round6(q.x - x0), y: round6(q.y - y0) }));
  let k = 0;
  ring.forEach((q, i) => { const b = ring[k]; if (q.y < b.y - EPS || (close(q.y, b.y) && q.x < b.x)) k = i; });
  return [...ring.slice(k), ...ring.slice(0, k)];
}

const sameRing = (a: Pt[], b: Pt[]) =>
  a.length === b.length && a.every((q, i) => close(q.x, b[i].x) && close(q.y, b[i].y));

/**
 * The outline of a union of axis-aligned rectangles, as one ring with positive
 * winding starting at the min-y, min-x corner. Null when the union is not one
 * simple ring (disconnected, a hole, or two parts touching only at a corner).
 */
export function unionOutline(rects: Rect[]): Pt[] | null {
  const uniq = (v: number[]) => v.sort((a, b) => a - b).filter((x, i, s) => i === 0 || !close(x, s[i - 1]));
  const xs = uniq(rects.flatMap((r) => [r.x0, r.x1]));
  const ys = uniq(rects.flatMap((r) => [r.y0, r.y1]));
  const nx = xs.length - 1, ny = ys.length - 1;
  const cov = (i: number, j: number) => {
    if (i < 0 || j < 0 || i >= nx || j >= ny) return false;
    const cx = (xs[i] + xs[i + 1]) / 2, cy = (ys[j] + ys[j + 1]) / 2;
    return rects.some((r) => cx > r.x0 && cx < r.x1 && cy > r.y0 && cy < r.y1);
  };
  const key = (p: Pt) => `${round6(p.x)},${round6(p.y)}`;
  const edges = new Map<string, Pt[]>();
  let count = 0;
  const add = (a: Pt, b: Pt) => {
    const k = key(a);
    if (!edges.has(k)) edges.set(k, []);
    edges.get(k)!.push(b);
    count++;
  };
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      if (!cov(i, j)) continue;
      const x0 = xs[i], x1 = xs[i + 1], y0 = ys[j], y1 = ys[j + 1];
      if (!cov(i, j - 1)) add({ x: x0, y: y0 }, { x: x1, y: y0 });
      if (!cov(i + 1, j)) add({ x: x1, y: y0 }, { x: x1, y: y1 });
      if (!cov(i, j + 1)) add({ x: x1, y: y1 }, { x: x0, y: y1 });
      if (!cov(i - 1, j)) add({ x: x0, y: y1 }, { x: x0, y: y0 });
    }
  }
  if (!count) return null;
  for (const v of edges.values()) if (v.length > 1) return null;
  let start: Pt | null = null;
  for (const k of edges.keys()) {
    const [x, y] = k.split(',').map(Number);
    if (!start || y < start.y - EPS || (close(y, start.y) && x < start.x)) start = { x, y };
  }
  const ring: Pt[] = [];
  let cur = start!;
  for (let guard = 0; guard <= count; guard++) {
    ring.push(cur);
    const nxt = edges.get(key(cur))![0];
    if (close(nxt.x, start!.x) && close(nxt.y, start!.y)) break;
    cur = nxt;
  }
  if (ring.length !== count) return null;
  return normalizeRing(ring);
}

/** A 4-corner orthogonal ring as a rectangle. */
export function asRectangle(ring: Pt[]): Rect | null {
  const r = canonicalRing(ring);
  if (r.length !== RECT_CORNERS) return null;
  for (let i = 0; i < RECT_CORNERS; i++) { const t = axisOf(r[i], r[(i + 1) % RECT_CORNERS]); if (t !== 'h' && t !== 'v') return null; }
  const xs = r.map((q) => q.x), ys = r.map((q) => q.y);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/**
 * A 6-corner orthogonal L as [bar, stub], the same decomposition as
 * app/domain/outline_capture.py::as_l_shape (full column merged vertically).
 */
export function asLShape(ring: Pt[]): [Rect, Rect] | null {
  const r = canonicalRing(ring);
  if (r.length !== L_CORNERS) return null;
  for (let i = 0; i < L_CORNERS; i++) { const t = axisOf(r[i], r[(i + 1) % L_CORNERS]); if (t !== 'h' && t !== 'v') return null; }
  const uniq = (v: number[]) => v.sort((a, b) => a - b).filter((x, i, s) => i === 0 || !close(x, s[i - 1]));
  const xs = uniq(r.map((q) => q.x)), ys = uniq(r.map((q) => q.y));
  if (xs.length !== L_GRID_VALUES || ys.length !== L_GRID_VALUES) return null;
  const present: [number, number][] = [];
  let missing: [number, number] | null = null;
  for (let i = 0; i < 2; i++) {
    for (let j = 0; j < 2; j++) {
      const c = { x: (xs[i] + xs[i + 1]) / 2, y: (ys[j] + ys[j + 1]) / 2 };
      if (inPolyPts(r, c)) present.push([i, j]); else missing = [i, j];
    }
  }
  if (present.length !== L_GRID_VALUES || !missing) return null;
  const [mi, mj] = missing;
  const fullCol = 1 - mi, fullRow = 1 - mj;
  const bar = { x0: xs[fullCol], y0: ys[0], x1: xs[fullCol + 1], y1: ys[2] };
  const stub = { x0: xs[mi], y0: ys[fullRow], x1: xs[mi + 1], y1: ys[fullRow + 1] };
  return [bar, stub];
}

export type V1Fields = {
  primary?: RectGeom;
  second?: RectGeom;
  openings?: OpeningV1[];
  subrooms?: RoomGeometryV1[];
};

/** What `fromV2ToV1Fields` found. */
export type V1Shape = 'carried' | 'rectangle' | 'l_shape' | 'none';

/**
 * v1 fields for a v2 document. `shape` says how primary/second were obtained;
 * 'none' means the outline is not a rectangle or an L (T, U, ...), so there is
 * no primary and the backend must read the polygon.
 */
export function fromV2ToV1Fields(v2: RoomGeometryV2): V1Fields & { shape: V1Shape } {
  const out: V1Fields & { shape: V1Shape } = { shape: 'none' };
  const height = v2.ceilingFromDefault ? undefined : round6(v2.ceilingFt);
  const ring = normalizeRing(v2.corners);
  const carriedRects = v2.primary ? rectsFromV1(v2) : null;
  const carriedRing = carriedRects ? unionOutline(carriedRects) : null;
  if (v2.primary && carriedRing && sameRing(carriedRing, ring)) {
    const origPh = v2.primary.height;
    const primary = clone(v2.primary);
    if (height != null) primary.height = height; else delete primary.height;
    out.primary = primary;
    if (v2.second) {
      const second = clone(v2.second);
      const ownCeiling = second.height != null && origPh != null && second.height !== origPh;
      if (!ownCeiling) {
        if (height == null) delete second.height;
        else if (second.height != null) second.height = height;
      }
      out.second = second;
    }
    out.shape = 'carried';
  } else {
    const rd = (r: Rect): RectGeom => {
      const g: RectGeom = { length: round6(r.x1 - r.x0), width: round6(r.y1 - r.y0) };
      if (height != null) g.height = height;
      return g;
    };
    const rect = asRectangle(ring);
    const L = rect ? null : asLShape(ring);
    if (rect) {
      out.primary = rd(rect);
      out.shape = 'rectangle';
    } else if (L) {
      const [bar, stub] = L;
      out.primary = rd(bar);
      out.second = { ...rd(stub), offsetX: round6(stub.x0 - bar.x0), offsetY: round6(stub.y0 - bar.y0) };
      out.shape = 'l_shape';
    }
  }
  const ops: OpeningV1[] = [...clone(v2.unplaced ?? [])];
  for (const o of v2.openings) {
    const row: OpeningV1 = { type: o.kind === 'missing' ? 'opening' : o.kind, width: round6(o.widthFt) };
    if (o.heightFt != null) row.height = round6(o.heightFt);
    ops.push(row);
  }
  if (ops.length) out.openings = ops;
  if (v2.subrooms) out.subrooms = clone(v2.subrooms);
  return out;
}

/** v1 fields stripped of `shape`, ready to spread into a v2 document. */
function v1FieldsForDoc(v2: RoomGeometryV2): Pick<RoomGeometryV2, 'primary' | 'second'> {
  const f = fromV2ToV1Fields(v2);
  const out: Pick<RoomGeometryV2, 'primary' | 'second'> = {};
  if (f.primary) out.primary = f.primary;
  if (f.second) out.second = f.second;
  return out;
}

/**
 * Measurements implied by typed v1 dimensions: per axis, consecutive
 * coordinate groups are tied, so a converted room opens fully measured (its
 * numbers were typed, not drawn).
 */
function measurementsFor(corners: Corner[]): Measurement[] {
  const room = { corners, measurements: [] } as unknown as Room;
  const U = buildUF(room, false);
  const out: Measurement[] = [];
  const n = corners.length;
  const isEdge = (a: number, b: number) => corners.some((c, i) => {
    const d = corners[(i + 1) % n];
    return (c.id === a && d.id === b) || (c.id === b && d.id === a);
  });
  for (const ax of ['x', 'y'] as Axis[]) {
    const groups = new Map<number, Corner>();
    corners.forEach((c) => { const r = U.find(ax, c.id); if (!groups.has(r)) groups.set(r, c); });
    const reps = [...groups.values()].sort((a, b) => a[ax] - b[ax]);
    for (let k = 0; k + 1 < reps.length; k++) {
      const a = reps[k], b = reps[k + 1];
      out.push({ axis: ax, a: a.id, b: b.id, valueFt: round6(b[ax] - a[ax]), kind: isEdge(a.id, b.id) ? 'len' : 'across', source: MANUAL_SOURCE });
    }
  }
  return out;
}

export interface ToV2Options { name?: string; level?: string }

/** v1 -> v2 per D8. Never reshapes silently: a fallback carries a banner. */
export function toV2(v1: RoomGeometryV1, opts: ToV2Options = {}): RoomGeometryV2 {
  const ph = num(v1.primary?.height);
  const doc: RoomGeometryV2 = {
    version: 2,
    geometryVersion: GEOMETRY_VERSION, // FIELD-4 K12: converted by this build
    level: opts.level ?? POLICY.level_default,
    ceilingFt: ph ?? POLICY.ceiling_default_ft,
    corners: [],
    measurements: [],
    openings: [],
  };
  if (opts.name != null) doc.name = opts.name;
  if (ph == null) doc.ceilingFromDefault = true;
  const banners: string[] = [];
  const rects = rectsFromV1(v1);
  let ring: Pt[] | null = null;
  if (!rects) {
    banners.push('No room size was captured. This is the default rectangle, not a measurement.');
    ring = shapePts('rect').map(([x, y]) => ({ x, y }));
  } else if (rects.length === 1) {
    ring = unionOutline(rects);
    if (v1.second) banners.push('The second rectangle had no usable size, so the room opened as its main rectangle.');
  } else {
    const [a, b] = rects;
    const ox = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0), oy = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
    const sharesEdge = (close(ox, 0) && oy > EPS) || (close(oy, 0) && ox > EPS);
    ring = sharesEdge ? unionOutline(rects) : null;
    if (!ring) {
      ring = unionOutline([a]);
      banners.push(
        ox > EPS && oy > EPS
          ? 'The two rectangles overlap, so the room opened as its main rectangle. Redraw the second part.'
          : 'The second rectangle does not share an edge with the main one, so the room opened as its main rectangle. Redraw the second part.',
      );
    }
    const sh = num(v1.second?.height);
    if (sh != null && ph != null && !close(sh, ph)) {
      banners.push(`The second part had its own ceiling height (${sh} ft); the room uses ${ph} ft.`);
    }
  }
  let id = 1;
  doc.corners = ring!.map((p) => ({ id: id++, x: round6(p.x), y: round6(p.y) }));
  if (rects) doc.measurements = measurementsFor(doc.corners);
  if (v1.primary) doc.primary = clone(v1.primary);
  if (v1.second) doc.second = clone(v1.second);
  if (v1.openings?.length) {
    doc.unplaced = clone(v1.openings);
    banners.push(`${v1.openings.length} opening${v1.openings.length > 1 ? 's' : ''} from the old room editor need a wall. Tap one to place it.`);
  }
  if (v1.subrooms) doc.subrooms = clone(v1.subrooms);
  if (banners.length) doc.banner = banners.join(' ');
  return doc;
}

/** Either model in, v2 out. */
export function anyToV2(g: RoomGeometryV1 | RoomGeometryV2, opts: ToV2Options = {}): RoomGeometryV2 {
  return (g as RoomGeometryV2).version === 2 ? clone(g as RoomGeometryV2) : toV2(g as RoomGeometryV1, opts);
}

const CARRY: (keyof Room & keyof RoomGeometryV2)[] = [
  'ceilingFromDefault', 'banner', 'unplaced', 'subrooms', 'primary', 'second', 'walls', 'ceiling', 'checkDims', 'spans',
  'attachedEdge', // FIELD-3 J18 (walls lane)
];

/** A v2 document as a core room. `id` is the OfflineRoom id. */
export function roomFromV2(id: string, v2: RoomGeometryV2, fallbackName = POLICY.new_room_name): Room {
  const r: Room = {
    id,
    name: v2.name ?? fallbackName,
    level: v2.level,
    ceilingFt: v2.ceilingFt,
    corners: clone(v2.corners),
    measurements: clone(v2.measurements),
    openings: clone(v2.openings),
  };
  for (const k of CARRY) if (v2[k] !== undefined) (r as unknown as Record<string, unknown>)[k] = clone(v2[k]);
  return r;
}

/** A core room as a v2 document, with v1 primary/second when expressible. */
export function roomToV2(r: Room): RoomGeometryV2 {
  const doc: RoomGeometryV2 = {
    version: 2,
    // FIELD-4 K12: every room this build writes carries this build's geometry schema.
    geometryVersion: GEOMETRY_VERSION,
    name: r.name,
    level: r.level,
    ceilingFt: r.ceilingFt,
    corners: clone(r.corners),
    measurements: clone(r.measurements),
    openings: clone(r.openings),
  };
  for (const k of CARRY) if (r[k] !== undefined) (doc as unknown as Record<string, unknown>)[k] = clone(r[k]);
  delete doc.primary;
  delete doc.second;
  const v1 = v1FieldsForDoc({ ...doc, primary: r.primary, second: r.second });
  if (v1.primary) doc.primary = v1.primary;
  if (v1.second) doc.second = v1.second;
  return doc;
}

export interface StoredRoom { id: string; name?: string; geometry?: RoomGeometryV1 | RoomGeometryV2 | null }

/**
 * Assemble a plan from stored rooms (v1 or v2). Rooms without geometry are
 * skipped and listed, not drawn. FIELD-1 rooms (no `walls` map) are migrated
 * (FIELD-2 E6, `migrateLegacyWalls`): nothing moves, and storage is not
 * rewritten until the room changes. Doors get the FIELD-3 fields (`migrateDoors`).
 */
export function planFromStoredRooms(rooms: StoredRoom[], defaults?: PlanDefaults): { plan: Plan; skipped: string[] } {
  const plan = blankPlan(defaults);
  const skipped: string[] = [];
  for (const s of rooms) {
    if (!s.geometry) { skipped.push(s.id); continue; }
    const v2 = anyToV2(s.geometry, { name: s.name });
    plan.rooms.push(roomFromV2(s.id, v2, s.name));
  }
  // FIELD-3 re-review: the app-only records are validated before anything reads them.
  sanitizePlan(plan);
  migrateLegacyWalls(plan);
  // FIELD-3 item 2.4: doors saved before FIELD-3 get panels, bifold side, cased, stained.
  migrateDoors(plan);
  reindexNext(plan);
  return { plan, skipped };
}

/** Every room of the plan as a v2 document, keyed by room id. */
export function planToV2Rooms(plan: Plan): { id: string; geometry: RoomGeometryV2 }[] {
  return plan.rooms.map((r) => ({ id: r.id, geometry: roomToV2(r) }));
}

/**
 * Place one of a room's unplaced v1 openings on a wall (the screen's "tap to
 * place"). It becomes a normal opening with the v1 width and height.
 */
export function placeUnplaced(
  plan: Plan, rid: string, index: number, vid: number, off: number,
): { ok: true; oid: number } | Fail<'missing' | 'too-short'> {
  const r = findRoom(plan, rid);
  const u = r?.unplaced?.[index];
  if (!r || !u) return { ok: false, err: 'missing', message: 'That opening is gone' };
  const kind = u.type === 'opening' ? 'missing' : u.type;
  const res = addOpening(plan, rid, vid, off, kind);
  if (!res.ok) return res;
  const patch: { widthFt?: number; heightFt?: number } = {};
  if (u.width != null) patch.widthFt = u.width;
  if (u.height != null && kind !== 'missing') patch.heightFt = u.height;
  const up = updateOpening(plan, rid, res.oid, patch);
  if (!up.ok) {
    // The captured width does not fit this wall: undo the add, keep it unplaced.
    for (const q of plan.rooms) q.openings = q.openings.filter((o) => !(o.id === res.oid && q.id === rid) && !(res.link != null && o.link === res.link));
    return { ok: false, err: 'too-short', message: up.message };
  }
  r.unplaced = r.unplaced!.filter((_, i) => i !== index);
  return { ok: true, oid: res.oid };
}

/** Feet <-> inches for display; export conversions live in the backend. */
export const ftToIn = (ft: number): number => ft * IN_PER_FT;
